'use client';

import { Suspense, useState, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';
import BrandStory from '@/components/BrandStory';
import LoadingSpinner from '@/components/LoadingSpinner';
import AgreementDrawer from '@/components/AgreementDrawer';
import SiteBeianLinks from '@/components/SiteBeianLinks';
import InviteContextBanner from '@/components/album/InviteContextBanner';
import api from '@/lib/api';
import { getBrandHomeUrl, getCreatorRegisterUrl } from '@/lib/creatorPlatformUrls';
import { getInviteTokenFromSearch, getPostAuthRedirect, appendQueryParams } from '@/lib/postAuthRedirect';

function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirectPath = getPostAuthRedirect(searchParams);
  const inviteToken = getInviteTokenFromSearch(searchParams);
  const prefilledEmail = searchParams.get('email')?.trim() || '';
  const [email, setEmail] = useState(prefilledEmail);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [agreementDrawerOpen, setAgreementDrawerOpen] = useState(false);
  const [agreementType, setAgreementType] = useState<'terms-of-service' | 'privacy-policy'>('terms-of-service');
  const { login, loading: authLoading, user } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setEmailNotVerified(false);
    setResendSuccess(false);
    setLoading(true);

    try {
      await login(email, password, { redirect: redirectPath, inviteToken });
    } catch (error: any) {
      if (error.email_verified === false || error.data?.email_verified === false) {
        setEmailNotVerified(true);
        setError('请先验证邮箱地址。请查收验证邮件并点击链接。如未收到，请检查垃圾邮件文件夹。');
      } else {
        const msg = error.message || '登录失败，请重试';
        // 若为 500 且提示像连接/网络问题，提示可能未启动后端
        const hint = (error.data?.details && /ECONNREFUSED|fetch|network|连接/i.test(String(error.data.details)))
          ? '（请确认后端已启动：在项目根目录执行 npm run backend:dev 或 npm run dev:full）'
          : '';
        setError(hint ? `${msg} ${hint}` : msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      setError('请先输入邮箱地址');
      return;
    }

    setResendingEmail(true);
    setError('');
    setResendSuccess(false);

    try {
      await api.auth.resendVerification(email);
      setResendSuccess(true);
      setError('');
    } catch (error: any) {
      setError(error.message || '重新发送验证邮件失败，请重试');
      setResendSuccess(false);
    } finally {
      setResendingEmail(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  useEffect(() => {
    if (user && !authLoading) {
      router.replace(redirectPath);
    }
  }, [user, authLoading, router, redirectPath]);

  useEffect(() => {
    if (prefilledEmail) setEmail(prefilledEmail);
  }, [prefilledEmail]);

  if (user) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '2rem'
      }}>
        <LoadingSpinner size="large" showText={false} />
      </div>
    );
  }

  if (authLoading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '2rem'
      }}>
        <LoadingSpinner size="large" showText={false} />
      </div>
    );
  }

  return (
    <main className="mobile-layout">
      <div className="mobile-left">
        <div className="mobile-content">
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '3rem'
          }}>
            <Link href={getBrandHomeUrl()} style={{ textDecoration: 'none' }}>
              <img
                src="/logo.png"
                alt="基音"
                loading="eager"
                fetchPriority="high"
                style={{
                  width: '45px',
                  height: '45px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.8';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              />
            </Link>
            <div style={{ textAlign: 'right' }}>
              <div style={{
                fontSize: '15px',
                fontWeight: '600',
                color: '#000000',
                lineHeight: '1.2',
                marginBottom: '2px',
                letterSpacing: '0.3px'
              }}>基音</div>
              <div style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#000000',
                letterSpacing: '1px',
                textTransform: 'uppercase'
              }}>FUNDAMUSE</div>
            </div>
          </div>

          <h1 className="mobile-title">登录账户</h1>
          <p className="mobile-subtitle">欢迎回到基音，和世界一起共创音乐</p>

          <InviteContextBanner inviteToken={inviteToken} />

          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label className="form-label">邮箱地址</label>
              <input
                type="email"
                placeholder="请输入邮箱地址"
                className="input-field mobile-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">密码</label>
              <div className="password-container">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="请输入密码"
                  className="input-field mobile-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={togglePasswordVisibility}
                  aria-label={showPassword ? '隐藏密码' : '显示密码'}
                >
                  {showPassword ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div style={{
                color: '#ff4444',
                fontSize: '14px',
                marginBottom: '1rem',
                padding: '0.75rem',
                backgroundColor: '#fff5f5',
                borderRadius: '4px',
                border: '1px solid #fed7d7'
              }}>
                <div style={{ marginBottom: emailNotVerified ? '0.5rem' : '0' }}>{error}</div>
                {emailNotVerified && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <button
                      type="button"
                      onClick={handleResendVerification}
                      disabled={resendingEmail}
                      style={{
                        background: 'transparent',
                        border: '1px solid #ff4444',
                        color: '#ff4444',
                        padding: '0.375rem 0.75rem',
                        borderRadius: '4px',
                        fontSize: '12px',
                        cursor: resendingEmail ? 'not-allowed' : 'pointer',
                        marginTop: '0.25rem',
                        fontWeight: '500'
                      }}
                    >
                      {resendingEmail ? '发送中...' : '重新发送验证邮件'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {resendSuccess && (
              <div style={{
                color: '#22c55e',
                fontSize: '14px',
                marginBottom: '1rem',
                padding: '0.5rem',
                backgroundColor: '#f0fdf4',
                borderRadius: '4px',
                border: '1px solid #86efac'
              }}>
                验证邮件已重新发送，请查收您的邮箱（包括垃圾邮件文件夹）
              </div>
            )}

            <div className="form-options">
              <label className="remember-me-text">
                <input type="checkbox" className="custom-checkbox" />
                记住我
              </label>
              <Link href="/forgot-password" className="link-hover">忘记密码？</Link>
            </div>

            <button
              type="submit"
              className="login-button mobile-button"
              disabled={loading}
            >
              {loading ? '登录中...' : '登录'}
            </button>
          </form>

          <div className="register-guide">
            <p className="register-text">
              还没有账户？{' '}
              <a
                href={appendQueryParams(getCreatorRegisterUrl(), {
                  invite: inviteToken,
                  email: email || prefilledEmail,
                  redirect: redirectPath !== '/dashboard' ? redirectPath : undefined
                })}
                className="link-hover"
              >
                立即注册
              </a>
            </p>
          </div>

          <p className="terms-text">
            继续即表示您同意我们的{' '}
            <a
              href="#"
              className="link-hover"
              onClick={(e) => {
                e.preventDefault();
                setAgreementType('terms-of-service');
                setAgreementDrawerOpen(true);
              }}
            >
              服务条款
            </a>
            {' '}和{' '}
            <a
              href="#"
              className="link-hover"
              onClick={(e) => {
                e.preventDefault();
                setAgreementType('privacy-policy');
                setAgreementDrawerOpen(true);
              }}
            >
              隐私政策
            </a>
          </p>

          <SiteBeianLinks className="auth-beian-links site-beian-links" />

          <AgreementDrawer
            isOpen={agreementDrawerOpen}
            onClose={() => setAgreementDrawerOpen(false)}
            agreementType={agreementType}
          />
        </div>
      </div>

      <div className="mobile-divider" />
      <BrandStory />
    </main>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: '2rem' }}>
        <LoadingSpinner size="large" showText={false} />
      </div>
    }>
      <LoginForm />
    </Suspense>
  );
}
